import { createFileRoute, Link, notFound, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { zodValidator } from "@tanstack/zod-adapter";
import { themes, priceBuckets, type Theme, type Photographer } from "@/data/photographers";
import { fetchAllPhotographers } from "@/lib/photographers-source";
import { PortraitImage } from "@/components/PortraitImage";
import { PhotoLightbox, type LightboxInfo } from "@/components/PhotoLightbox";
import { CreatorFilterBar } from "@/components/CreatorFilterBar";
import { creatorFiltersSchema, matchesCreatorFilters, type CreatorFilters } from "@/lib/creator-filters";
import { useTranslation } from "react-i18next";

type ThemeData = (typeof themes)[number];

const ALL_THEME: ThemeData = {
  id: "all" as Theme,
  label: "Alle",
  description: "Alle Bilder aus allen Themen, die komplette Auswahl.",
};

export const Route = createFileRoute("/themes/$theme")({
  validateSearch: zodValidator(creatorFiltersSchema),
  loader: ({ params }) => {
    if (params.theme === "all") return { theme: ALL_THEME };
    const theme = themes.find((t) => t.id === params.theme);
    if (!theme) throw notFound();
    return { theme };
  },
  head: ({ loaderData, params }) => {
    const t = loaderData?.theme;
    const url = `https://omselected.art/themes/${params.theme}`;
    return {
      meta: t
        ? [
            { title: `${t.label}, OM Selected` },
            { name: "description", content: `${t.label}: ${t.description}` },
            { property: "og:title", content: `${t.label}, OM Selected` },
            { property: "og:description", content: t.description },
            { property: "og:url", content: url },
          ]
        : [{ title: "Theme, OM Selected" }],
      links: t ? [{ rel: "canonical", href: url }] : undefined,
      scripts: t
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "CollectionPage",
                name: `${t.label}, OM Selected`,
                description: t.description,
                url,
                isPartOf: { "@type": "WebSite", name: "OM Selected", url: "https://omselected.art" },
              }),
            },
          ]
        : undefined,
    };
  },
  notFoundComponent: () => {
    const { t } = useTranslation();
    return (
      <div className="mx-auto max-w-[800px] px-5 py-32 text-center">
        <h1 className="serif text-4xl">{t("theme.notFound")}</h1>
        <Link to="/themes/$theme" params={{ theme: "portraits" }} className="eyebrow inline-block mt-8 border-b border-foreground pb-1">{t("theme.viewPortraits")}</Link>
      </div>
    );
  },
  errorComponent: ({ error }) => {
    const { t } = useTranslation();
    return (
      <div className="mx-auto max-w-[800px] px-5 py-32 text-center">
        <h1 className="serif text-4xl">{t("profile.error")}</h1>
        <p className="mt-4 text-muted-foreground">{error.message}</p>
      </div>
    );
  },
  component: ThemePage,
});

function ThemePage() {
  const { t: tr } = useTranslation();
  const { theme } = Route.useLoaderData() as { theme: ThemeData };
  const isAll = theme.id === ("all" as Theme);
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [all, setAll] = useState<Photographer[]>([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const PAGE_SIZE = 12;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [landscape, setLandscape] = useState<Record<string, boolean>>({});

  const updateFilters = (patch: Partial<CreatorFilters>) =>
    navigate({ search: (prev: CreatorFilters) => ({ ...prev, ...patch }), replace: true });
  const resetFilters = () =>
    navigate({
      search: { country: "", city: "", theme: "", price: "", setting: "", language: "", page: 1 },
      replace: true,
    });

  useEffect(() => {
    let active = true;
    fetchAllPhotographers().then((data) => {
      if (active) {
        setAll(data);
        setLoading(false);
      }
    });
    return () => { active = false; };
  }, []);

  const themeMatches = isAll
    ? all
    : all.filter((p) => p.themes.includes(theme.id as Theme));
  const matching = themeMatches.filter((p) =>
    matchesCreatorFilters(p, search, priceBuckets),
  );
  const cityOptions = Array.from(
    new Set(
      themeMatches
        .map((p) => p.city ?? p.region)
        .filter((c): c is string => !!c && c !== ""),
    ),
  ).sort();
  const works = useMemo(() => {
    const items = all.flatMap((p) =>
      (p.gallery ?? [])
        .map((src, i) => {
          const themesForSlot = (p.galleryThemesAll?.[i] ?? [p.galleryThemes?.[i] ?? "open"]) as Theme[];
          return {
            id: `${p.id}-${i}`,
            image: src,
            caption: p.name,
            photographerId: p.id,
            photographerName: p.name,
            region: p.city ?? p.region,
            theme: themesForSlot[0],
            themes: themesForSlot,
            instagramHandle: p.instagramHandle ?? null,
            websiteUrl: p.websiteUrl ?? null,
          };
        })
        .filter((w) => isAll || w.themes.includes(theme.id as Theme))
    );
    // Shuffle on every fresh load / theme change
    for (let i = items.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [items[i], items[j]] = [items[j], items[i]];
    }
    return items;
  }, [all, isAll, theme.id]);
  const makeInfo = (w: (typeof works)[number]): LightboxInfo => ({
    image: w.image,
    caption: w.caption,
    photographerName: w.photographerName,
    photographerId: w.photographerId,
    region: w.region,
    theme: theme.label,
    instagramHandle: w.instagramHandle,
    websiteUrl: w.websiteUrl,
    creatorType: "photographer" as const,
  });
  const visibleWorks = works.slice(0, visibleCount);
  const hasMore = visibleCount < works.length;
  // Reset visible count when theme changes
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [theme.id]);
  return (
    <div>
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 pt-12 pb-16">
          <div className="flex gap-6 eyebrow mb-8">
            <Link
              to="/themes/$theme"
              params={{ theme: "portraits" }}
              className="text-foreground border-b border-foreground pb-1"
            >
              {tr("theme.creatorTypePhotographers")}
            </Link>
            <Link
              to="/visagists/themes/$theme"
              params={{ theme: "day-makeup" }}
              className="text-muted-foreground hover:text-foreground"
            >
              {tr("theme.creatorTypeVisagists")}
            </Link>
          </div>
          <div className="eyebrow text-muted-foreground">{tr("theme.eyebrow")}</div>
          <h1 className="serif text-6xl md:text-8xl mt-6 leading-[0.9]">{theme.label}</h1>
          <p className="serif italic text-xl md:text-2xl mt-8 max-w-xl">{theme.description}</p>
          <div className="mt-10 flex flex-wrap gap-4 eyebrow text-muted-foreground">
            <Link
              to="/themes/$theme"
              params={{ theme: "all" }}
              className={isAll ? "text-foreground border-b border-foreground pb-1" : "hover:text-foreground"}
            >
              {tr("theme.all")}
            </Link>
            {themes.map((t) => (
              <Link key={t.id} to="/themes/$theme" params={{ theme: t.id }} className={t.id === theme.id ? "text-foreground border-b border-foreground pb-1" : "hover:text-foreground"}>
                {t.label}
              </Link>
            ))}
          </div>
        </div>
      </section>
      {visibleWorks.length > 0 && (
        <section className="border-b border-border">
          <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-16">
            <h2 className="eyebrow text-muted-foreground mb-8 font-normal">{tr("theme.selected")}</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 [grid-auto-flow:dense]">
              {visibleWorks.map((w, i) => (
                <button
                  key={w.id}
                  type="button"
                  onClick={() => setLightboxIndex(i)}
                  className={`block group text-left w-full ${landscape[w.id] ? "col-span-2" : ""}`}
                >
                  <figure>
                    <img
                      src={w.image}
                      alt={`${theme.label} photography by ${w.photographerName}${w.caption ? ` — ${w.caption}` : ""}`}
                      loading="lazy"
                      onLoad={(e) => {
                        const img = e.currentTarget;
                        if (img.naturalWidth > img.naturalHeight * 1.1) {
                          setLandscape((prev) => (prev[w.id] ? prev : { ...prev, [w.id]: true }));
                        }
                      }}
                      className="w-full h-auto cursor-zoom-in bg-muted"
                    />
                    <figcaption className="mt-2 text-xs italic text-muted-foreground group-hover:text-foreground">{w.caption}</figcaption>
                  </figure>
                </button>
              ))}
            </div>
            {hasMore && (
              <div className="mt-12 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                  className="eyebrow border-b border-foreground pb-1 hover:opacity-70 transition-opacity"
                >
                  {tr("theme.loadMore", { count: works.length - visibleCount })}
                </button>
              </div>
            )}
          </div>
        </section>
      )}
      <section>
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-16">
          <h2 className="eyebrow text-muted-foreground mb-8 font-normal">{tr("theme.photographersInCategory")}</h2>
          <div className="border-y border-border bg-secondary/40 -mx-5 md:-mx-10 mb-10">
            <CreatorFilterBar
              values={search}
              onChange={updateFilters}
              onReset={resetFilters}
              resultsCount={matching.length}
              show={{
                city: cityOptions.map((c) => ({ value: c, label: c })),
                price: priceBuckets.map((b) => ({ value: b.id, label: b.label })),
                setting: true,
                language: true,
              }}
            />
          </div>
          {loading ? (
            <div className="text-xs text-muted-foreground italic">{tr("common.loading")}</div>
          ) : matching.length === 0 ? (
            <div className="text-xs text-muted-foreground italic">{tr("theme.emptyPhotographers")}</div>
          ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
            {matching.map((p) => (
              <Link key={p.id} to="/photographers/$id" params={{ id: p.id }} className="block">
                <PortraitImage p={p} />
                <div className="serif text-xl mt-4">{p.name}</div>
                <div className="eyebrow text-muted-foreground mt-1">{p.region}</div>
              </Link>
            ))}
          </div>
          )}
        </div>
      </section>
      <PhotoLightbox
        info={lightboxIndex !== null && visibleWorks[lightboxIndex] ? makeInfo(visibleWorks[lightboxIndex]) : null}
        onClose={() => setLightboxIndex(null)}
        onPrev={lightboxIndex !== null && lightboxIndex > 0 ? () => setLightboxIndex((idx) => (idx ?? 0) - 1) : undefined}
        onNext={lightboxIndex !== null && lightboxIndex < visibleWorks.length - 1 ? () => setLightboxIndex((idx) => (idx ?? 0) + 1) : undefined}
        neighborImages={lightboxIndex !== null ? [visibleWorks[lightboxIndex - 1]?.image, visibleWorks[lightboxIndex + 1]?.image] : undefined}
      />
    </div>
  );
}